<script>
   /*************************************************************/
   /* Root component for any application                        */
   /* ----------------------------------                        */
   /* universal wrapper for any application                     */
   /* implements routing among tabs/containers                  */
   /* and session management                                    */
   /*************************************************************/

   import { onMount } from 'svelte';
   import { date2str, reviver, replacer } from '../utils';
   import Modal from '../../shared/components/Modal.svelte';

   let {
      WASM,                   // WASM promise from main app
      appID,                  // application ID (as string) is used for saving sessions to local storage
      appTitle,               // application title (for header)
      appShortTitle,          // application short title (for header)
      appSlogan,              // application slogan (for header)
      tabs = [],              // array with tab properties (name, label, component)
      isBeta = false,         // logical is this a beta version or not
      initSession,            // callback which initialize JSON with app session
      checkSession,           // callback which checks validity of the JSON with app session loaded by user
      resetSession,           // callback which leads to app reset via session (when you load a new one)
      info,                   // snippet with HTMl for information window
      footer,                 // snippet with HTML for footer
   } = $props();


   const dbName = 'mdatools-' + appID;                         // database name (appID will be used for storage name)

   let isInfoShown = $state(false);                   // show or not info modal
   let session = $state();                            // curren session
   let sessionMessage = $state('Current session ');   // message to show in session corer
   let sessionHideButtons = $state(false);            // hide or not session buttons

   /* shows session message */
   function showMessage(msg) {
      sessionMessage = msg;
      sessionHideButtons = true;
         setTimeout(() => {
         sessionMessage = 'Current session ';
         sessionHideButtons = false;
      }, 1000);
   }


   /* callback to open modal with app info */
   function showInfo() {
      isInfoShown = true;
   }

   /* callback to close modal with app info */
   function closeInfo() {
      isInfoShown = false;
   }

   /* callback to select tab */
   function selectTab(id) {
      session.tab = id;
   }

   /* callbacl to reset the session in database and in the app window */
   function resetSesstionStorage() {
      const request = indexedDB.open(dbName, 1);
      request.onsuccess = (event) => {
         const db = event.target.result;
         const tx = db.transaction(appID, "readwrite");
         const store = tx.objectStore(appID);
         store.delete("session");

         tx.oncomplete = () => {
            session = initSession();
            showMessage('Session was reinitialzed.');
         };

         tx.onerror = () => console.error("Failed to session from DB.");
      };
   }

   /* callback to save the current session to local storage */
   function backupSession(sessionStr, init) {
      window.localStorage.removeItem(appID + "-blocked");
      const request = indexedDB.open(dbName, 1);
      request.onsuccess = (event) => {
        const db = event.target.result;
        const tx = db.transaction(appID, "readwrite");
        const store = tx.objectStore(appID);
        store.put(sessionStr, "session");
        store.put(date2str(new Date()), "session-update");
      };
   }

   /* callback to restore last session from the local storage */
   function restoreSession() {

      new Promise((resolve, reject) => {
        const request = indexedDB.open(dbName, 1);
        request.onsuccess = (event) => {
            const db = event.target.result;
            const tx = db.transaction(appID, "readonly");
            const store = tx.objectStore(appID);
            const getRequest = store.get("session");
            getRequest.onsuccess = () => resolve(getRequest.result);
            getRequest.onerror = () => reject(getRequest.error);
        };
      })
      .then((data) => {
            setSession(data);
            sessionMessage = 'Last session is restored ';
            sessionHideButtons = true;
            setTimeout(() => {
               sessionMessage = 'Current session ';
               sessionHideButtons = false;
            }, 1500);
      })
      .catch((err) => {
         console.error(err);
         session = initSession();
      });
   }

   /* parse session string to JSON and set it as current app session */
   function setSession(str) {

      if( !str || str === 'undefined') {
         session = initSession();
         return;
      }

      const _session =  JSON.parse(str, reviver);
      session = checkSession(_session) ? _session : initSession(appID);
   }

   /* callback to download session object as JSON file */
   function downloadSession() {

      const dateStr = date2str(new Date());
      const fileName = appID + '-session-' + dateStr + '.json';
      session.date = dateStr;

      const str = JSON.stringify(session, replacer);
      const blob = new Blob([str], { type: 'text/json' });
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')

      a.setAttribute('href', url)
      a.setAttribute('download', fileName);
      a.click()
   }

   /* callback to load session from JSON file */
   function loadSession (e) {
      resetSession(session);
      const reader = new FileReader();
      reader.onload = () => {
         setSession(reader.result);
         e.target.value = '';
      };
      reader.readAsText(e.target.files[0]);
   }

   function confirmAndReset() {
      if (confirm("Are you sure? All current results and models will be lost in all tabs.")) {
         resetSesstionStorage();
      }
   }

   onMount(() => {
      const storeName = appID;
      const request = indexedDB.open(dbName, 1);

      request.onupgradeneeded = (event) => {
         const db = event.target.result;
         if (!db.objectStoreNames.contains(storeName)) {
            db.createObjectStore(storeName);
         }
      };

      request.onsuccess = (event) => {
         restoreSession();
      }
   });
</script>

{#if session}
<div class="page">

   <header class="page-header">
      <div class="header-group">
         <img class="logo" alt={appShortTitle} src="logo.svg" />
         <h1>{appTitle}{#if isBeta}<sup style="color:red">β</sup>{/if}</h1>
         <p>{appSlogan}</p>
      </div>
      <ul class="nav">
         {#each tabs as tab, i}
         <li class="nav-item" class:selected={session.tab === i} >
            <button onclick={() => selectTab(i)} onkeydown={() => selectTab(i)}>{tab.name}</button>
         </li>
         {/each}
      </ul>
   </header>

   <main>
      {#each tabs as tab, i}
      <div class:hidden={session.tab !== i} class="tab-content">
      <tab.component {WASM} {session} {backupSession}/>
      </div>
      {/each}
   </main>

   <footer>
      <div class="session" class:no-buttons={sessionHideButtons}>
         <span class="session-message">{sessionMessage}</span>
         <label title="load session from file" class="upload-button"><input type="file" onchange={loadSession}/>
            <svg xmlns="http://www.w3.org/2000/svg" version="2.0" width="20px" height="20px" viewBox="0 0 100 100">
            <line x1="50" y1="25" x2="50" y2="75"/>
            <line x1="30" y1="40" x2="50" y2="25"/>
            <line x1="70" y1="40" x2="50" y2="25"/>
            </svg>
         </label>
         <!-- svelte-ignore a11y_consider_explicit_label -->
         <button title="download session as a file" class="download-button" onclick={downloadSession}>
            <svg xmlns="http://www.w3.org/2000/svg" version="2.0" width="20px" height="20px" viewBox="0 0 100 100">
            <line x1="50" y1="25" x2="50" y2="75"/>
            <line x1="30" y1="60" x2="50" y2="75"/>
            <line x1="70" y1="60" x2="50" y2="75"/>
            </svg>
         </button>
         <!-- svelte-ignore a11y_consider_explicit_label -->
         <button title="reset all sessions" class="clear-button" onclick={confirmAndReset}>
            <svg xmlns="http://www.w3.org/2000/svg" version="2.0" width="20px" height="20px" viewBox="0 0 100 100">
            <line x1="25" y1="25" x2="75" y2="75"/>
            <line x1="25" y1="75" x2="75" y2="25"/>
            </svg>
         </button>
      </div>
      <div class="info">
      <button title="Show information about the app" class="button-info" onclick={showInfo} onkeydown={showInfo}>info</button>
      {@render footer()}
      </div>
   </footer>

   {#if isInfoShown}
   <Modal closeModal={closeInfo}>
      <div class="info-container">
      {@render info()}
      </div>
   </Modal>
   {/if}
</div>
{/if}


<style>

   .page {
      width: 100%;
      height: 100%;
      margin: 0 auto;
      padding: 0;

      min-width: 1020px;
      min-height: 590px;
      max-width: 1900px;

      display: flex;
      flex-direction: column;
   }

   header{
      display: flex;
      justify-content: flex-start;
      flex-direction: row;
      background: #f8f6f4;
   }

   .header-group {
      width: min-content;
      min-width: 400px;
      margin: 0 0 0 0.25rem;
      display: grid;
      align-items: center;
      grid-template-columns: min-content 1fr;
      grid-template-rows: min-content min-content min-content min-content;
      grid-template-areas:
         "logo ."
         "logo title"
         "logo subtitle"
         "logo .";
   }

   .logo{
      width: 3.5rem;
      margin: 0.25rem;
      padding: 0.5rem;
      grid-area: logo;
   }

   .header-group > h1{
      margin: 0;
      padding: 0;
      text-align: left;
      font-size: 1.6rem;
      color: #4e5052;
      white-space: nowrap;
      grid-area: title;
   }

   .header-group > p{
      margin: 0;
      padding: 0;
      text-align: left;
      font-size: 1.1rem;
      color: #7e8082;
      grid-area: subtitle;
   }

   main{
      flex-basis: auto;
      flex-grow: 1;
      flex-shrink: 1;
      box-sizing: border-box;
      padding: 0.2rem 0;
   }

   main > div{
      height: 100%;
   }

   footer {
      display: grid;
      grid-template-columns: 20% 60% 20%;
      grid-template-areas: "session info .";
      align-items: center;
      justify-content: center;
      font-size: 0.9rem;
      flex-basis: min-content;
      flex-grow: 0;
      flex-shrink: 0;
      background: #4e5052;
      color: #c0c0c0;
      padding: 0.5em;
   }

   footer .session {
      grid-area: session;
      padding-left: 0.5em;
      display: flex;
      align-items: center;
   }

   footer .session input {
      display: none;
   }

   footer .session .session-message {
      color: #c0c0c0;
      padding-right: 0.5em;
   }

   footer .session:hover .session-message {
      color: #fafafa
   }

   footer .session.no-buttons .clear-button,
   footer .session.no-buttons .upload-button,
   footer .session.no-buttons .download-button {
      display: none;
   }

   footer .session .clear-button,
   footer .session .upload-button,
   footer .session .download-button {
      font-size: 0.9rem;
      background: #5e6062;
      color: #c0c0c0;
      box-shadow: none;
      border: none;
      padding: 0em;
      margin: 0 0 0 0.2em;
      border-radius: 3px;
      border: none;
      height: 20px;
      box-sizing: border-box;
      display: inline-block;
   }

   footer .session .clear-button svg,
   footer .session .upload-button svg,
   footer .session .download-button svg{
      stroke-width: 0.75em;
      stroke-linecap: round;
      stroke-linejoin: round;
      stroke: #c0c0c0;
   }

   footer .session:hover .clear-button,
   footer .session:hover .upload-button,
   footer .session:hover .download-button{
      background: #35332d;
   }

   footer .session:hover .clear-button svg,
   footer .session:hover .upload-button svg,
   footer .session:hover .download-button svg{
      stroke: #fafafa;
   }

   footer .session .clear-button {
      margin-left: 0.5em;
   }

   footer .session .upload-button:hover,
   footer .session .download-button:hover {
      background-color: #6eb8ff;
   }

   footer .session .clear-button:hover {
      background-color: #dc143c;
   }


   footer .info {
      grid-area: info;
      display: flex;
      justify-content: center;
      align-items: center;
   }


   footer .info .button-info {
      cursor: default;
      border: none;
      box-shadow: none;
      background: transparent;
      font-weight: bold;
      color: #4e5052;
      border-radius: 0.2em;

      padding: 0.25em 0.5em;
      text-align: center;
      line-height: 1em;
      margin-left: 0.25em;
      margin-right: 0.5em;
      background-color: #f8ebc5;
   }

   footer .info .button-info:hover {
      background-color: #f7db89;
   }

   footer :global(p) {
      margin: 0 0.5em;
      text-align: center;
      padding: 0.15em;
   }

   footer :global(span),
   footer :global(a) {
      color: #f8ebc5
   }

   .hidden {
      display: none;
   }

   .nav,
   .nav * {
      user-select: none;
      -moz-user-select: none;
   }

   .nav {
      display: flex;
      flex-direction: row;
      justify-content: flex-end;
      list-style: none;
      margin: 0 100px 0 auto;
      padding: 0;
      align-self: flex-end;
   }

   .nav-item {
      list-style: none;
      margin: 0;
      border-bottom: 5px solid transparent;
   }

   .nav-item > button {
      cursor: pointer;
      padding: 0.5em 1em;
      border: none;
      box-shadow: none;
      background: transparent;
      font-size: inherit;
   }

   .nav-item > button > :global(span) {
      color: #888;
   }

   .nav-item:hover {
      border-bottom: 5px solid #80888f50;
   }

   .nav-item:hover > button {
      border-top-left-radius: 5px;
      border-top-right-radius: 5px;
      background: #00000010;
   }

   .nav-item.selected {
      border-bottom: 5px solid #80888f;
   }

   .nav-item.selected > button{
      font-weight: bold;
      cursor: default;
   }

   .nav-item.selected:hover {
      border-bottom: 5px solid #80888f;
   }


   @media (min-width: 1600px) {
      .page > header {
         border-bottom-right-radius: 0.25rem;
         border-bottom-left-radius: 0.25rem;
      }

      .page > footer {
         border-top-right-radius: 0.25rem;
         border-top-left-radius: 0.25rem;
      }
   }


   .info-container {
      width: 900px;
   }

   .info-container > :global(p) {
      font-size: 1.15em;
      line-height: 1.35em;
      padding: 0 1em 0.5em 1em;
      margin: 0;
   }

   .info-container > :global(.release-info) {
      font-size: 1em;
      color: #808080;
      line-height: 1.25em;
      padding: 0 1.2em 0.5em 1.2em;
      margin: 0;
   }

   .info-container > :global(h2) {
      font-size: 1.5em;
      line-height: 1.25em;
      padding: 0em 1rem 0.5em 1rem;
      margin: 0;
   }

   .info-container > :global(h3) {
      font-size: 1.15em;
      line-height: 1.25em;
      padding: 1rem 1em 0.25em 1em;
      margin: 0;
   }

   .info-container > :global(ul) {
      margin: 0;
   }

   .info-container > :global(ul > li) {
      font-size: 1.15em;
      line-height: 1.25em;
      padding: 0 0.5em 0.5em 0em;
      margin: 0;
   }

   .info-container > :global(ul > li > span) {
      font-size: 0.9em;
      color: #808080;
      line-height: 1.25em;
      padding: 0 0.25em;
      margin: 0;

   }

   .info-container :global(a) {
      color: #ae8032;
   }

   .info-container :global(a:hover) {
      color: #eb9707;
   }

   .info-container:last-child {
      padding-bottom: 1rem;
   }


</style>